import React, { useState } from "react";
import { motion } from "framer-motion";
import { Send, MessageCircle, User, Phone, Mail, Users, Calendar, Compass, CheckCircle2, FileText } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useApp } from "../context/AppContext";
import { useCustomerAuth } from "../context/CustomerAuthContext";

const initialForm = () => ({
    full_name: "",
    phone: "",
    email: "",
    group_size: 10,
    preferred_date: "",
    duration_days: 7,
    trip_type: "Umre",
    notes: "",
});

const GuideRequestForm = () => {
    const { settings } = useApp();
    const { customer } = useCustomerAuth();
    const [form, setForm] = useState(() => ({
        ...initialForm(),
        full_name: customer?.full_name || "",
        email: customer?.email || "",
        phone: customer?.phone || "",
    }));
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const update = (field, value) => setForm({ ...form, [field]: value });

    const wa = (settings.whatsapp_number || "").replace(/\D/g, "");
    const waLink = wa && submitted
        ? `https://wa.me/${wa}?text=${encodeURIComponent(
              `Merhaba, rehberlik hizmetiniz için talep gönderdim.\n\nAd: ${form.full_name}\nTelefon: ${form.phone}\nGrup: ${form.group_size} kişi\nTip: ${form.trip_type}\nTarih: ${form.preferred_date || "esnek"}\nSüre: ${form.duration_days} gün\n\n${form.notes || ""}`
          )}`
        : null;

    const onSubmit = async (e) => {
        e.preventDefault();
        if (submitting) return;
        if (form.full_name.trim().length < 2) return toast.error("Ad soyad girin");
        if (form.phone.replace(/\D/g, "").length < 10) return toast.error("Geçerli telefon girin (10+ rakam)");
        setSubmitting(true);
        try {
            await api.post("/guide-requests", {
                ...form,
                group_size: Number(form.group_size) || 1,
                duration_days: Number(form.duration_days) || null,
                email: form.email || null,
            });
            setSubmitted(true);
            toast.success("Talebiniz alındı! En kısa sürede size dönüş yapacağız.");
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(detail || "Talep gönderilemedi, lütfen tekrar deneyin");
        } finally {
            setSubmitting(false);
        }
    };

    if (submitted) {
        return (
            <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                className="border border-gold/50 bg-emerald_surface p-8 text-center max-w-2xl mx-auto"
                data-testid="guide-request-success"
            >
                <div className="w-14 h-14 mx-auto rounded-full bg-emerald-700/40 border border-emerald-500/60 flex items-center justify-center mb-4">
                    <CheckCircle2 className="w-7 h-7 text-emerald-300" />
                </div>
                <h3 className="font-serif text-3xl text-gold mb-2">Talebiniz Alındı</h3>
                <p className="text-bone/80 leading-relaxed mb-6">
                    En kısa sürede size dönüş yapacağız. Acil durumlar için WhatsApp üzerinden de bize ulaşabilirsiniz.
                </p>
                <div className="flex items-center justify-center gap-3 flex-wrap">
                    {waLink && (
                        <a href={waLink} target="_blank" rel="noreferrer" className="btn-gold" data-testid="wa-after-submit">
                            <MessageCircle className="w-4 h-4" /> WhatsApp'tan Devam Et
                        </a>
                    )}
                    <button onClick={() => { setForm(initialForm()); setSubmitted(false); }} className="btn-outline-gold" data-testid="submit-another">
                        Yeni Talep Gönder
                    </button>
                </div>
            </motion.div>
        );
    }

    return (
        <motion.form
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            onSubmit={onSubmit}
            className="border border-border_green bg-emerald_surface p-6 lg:p-8"
            data-testid="guide-request-form"
        >
            <div className="flex items-center gap-2 mb-1">
                <FileText className="w-4 h-4 text-gold" />
                <div className="text-xs uppercase tracking-[0.25em] text-gold">Rehberlik Talep Formu</div>
            </div>
            <h3 className="font-serif text-2xl sm:text-3xl mb-2">Bizimle Doğrudan İletişime Geçin</h3>
            <p className="text-muted_green text-sm mb-6">Grubunuz için ehil hocamızı hazırlayalım. Bilgileri doldurun, sizi arayalım.</p>

            <div className="grid md:grid-cols-2 gap-4">
                <div>
                    <label className="label-dark flex items-center gap-1.5"><User className="w-3 h-3 text-gold" /> Ad Soyad *</label>
                    <input required minLength={2} className="input-dark" value={form.full_name} onChange={(e) => update("full_name", e.target.value)} placeholder="Adınız Soyadınız" data-testid="gr-name" />
                </div>
                <div>
                    <label className="label-dark flex items-center gap-1.5"><Phone className="w-3 h-3 text-gold" /> Telefon *</label>
                    <input required type="tel" className="input-dark" value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="+90 555 ..." data-testid="gr-phone" />
                </div>
                <div>
                    <label className="label-dark flex items-center gap-1.5"><Mail className="w-3 h-3 text-gold" /> E-posta <span className="text-muted_green text-[10px] uppercase tracking-[0.15em] ml-1">(opsiyonel)</span></label>
                    <input type="email" className="input-dark" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="e-posta@ornek.com" data-testid="gr-email" />
                </div>
                <div>
                    <label className="label-dark flex items-center gap-1.5"><Users className="w-3 h-3 text-gold" /> Grup Büyüklüğü *</label>
                    <input required type="number" min={1} max={500} className="input-dark" value={form.group_size} onChange={(e) => update("group_size", e.target.value)} data-testid="gr-group" />
                </div>
                <div>
                    <label className="label-dark flex items-center gap-1.5"><Compass className="w-3 h-3 text-gold" /> Seyahat Türü</label>
                    <select className="input-dark" value={form.trip_type} onChange={(e) => update("trip_type", e.target.value)} data-testid="gr-type">
                        <option value="Umre">Umre</option>
                        <option value="Hac">Hac</option>
                        <option value="Ziyaret">Ziyaret / Kültür Gezisi</option>
                        <option value="Diğer">Diğer</option>
                    </select>
                </div>
                <div>
                    <label className="label-dark flex items-center gap-1.5"><Calendar className="w-3 h-3 text-gold" /> Tercih Ettiğiniz Tarih <span className="text-muted_green text-[10px] uppercase tracking-[0.15em] ml-1">(opsiyonel)</span></label>
                    <input type="date" className="input-dark" value={form.preferred_date} onChange={(e) => update("preferred_date", e.target.value)} data-testid="gr-date" />
                </div>
                <div>
                    <label className="label-dark">Yolculuk Süresi (gün)</label>
                    <input type="number" min={1} max={60} className="input-dark" value={form.duration_days} onChange={(e) => update("duration_days", e.target.value)} data-testid="gr-days" />
                </div>
            </div>

            <div className="mt-4">
                <label className="label-dark">Notlarınız / Özel İstekler</label>
                <textarea rows={4} className="input-dark" value={form.notes} onChange={(e) => update("notes", e.target.value)} placeholder="Grubunuz, tercih ettiğiniz hoca, dil, program özelinde belirtmek istedikleriniz..." data-testid="gr-notes" />
            </div>

            <div className="mt-6 flex items-center gap-3 flex-wrap">
                <button type="submit" disabled={submitting} className="btn-gold" data-testid="gr-submit">
                    <Send className="w-4 h-4" /> {submitting ? "Gönderiliyor…" : "Talep Gönder"}
                </button>
                <span className="text-xs text-muted_green">Ekibimiz iş saatleri içinde en fazla 24 saat içinde geri döner.</span>
            </div>
        </motion.form>
    );
};

export default GuideRequestForm;
